import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useAdminNotifications } from "../../context/AdminNotificationsContext";
import {
  Leaf, Headphones, ShoppingBag, Clock, Plus, Package,
  CheckCircle, XCircle, AlertCircle, ToggleRight, ShoppingCart, AlertTriangle
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatTile({ label, value, icon: Icon, color, link, sub }) {
  return (
    <Link to={link}>
      <div data-testid={`stat-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}
        className="p-4 rounded-xl border border-[#273A2F] hover:border-[#D97A46]/40 transition-all cursor-pointer group"
        style={{ backgroundColor: '#16201A' }}>
        <div className="mb-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}18` }}>
            <Icon className="w-4 h-4" style={{ color }} />
          </div>
        </div>
        <p className="font-serif text-2xl font-bold text-[#F2F4F3] leading-none">{value ?? '—'}</p>
        <p className="text-xs text-[#A8BCAE] mt-1 leading-snug">{label}</p>
        {sub && <p className="text-[10px] mt-0.5" style={{ color }}>{sub}</p>}
      </div>
    </Link>
  );
}

function SectionHeader({ title, link, linkLabel }) {
  return (
    <div className="flex items-center justify-between mb-3 mt-8 first:mt-0">
      <h2 className="text-xs font-semibold tracking-widest uppercase text-[#A8BCAE]">{title}</h2>
      {link && (
        <Link to={link} className="text-xs font-medium hover:underline" style={{ color: '#D97A46' }}>{linkLabel || 'Manage →'}</Link>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { authHeader } = useAuth();
  const { pending, refresh } = useAdminNotifications();
  const [stats, setStats] = useState(null);
  const [lowStock, setLowStock] = useState(null);

  useEffect(() => {
    const headers = authHeader();
    axios.get(`${API}/admin/stats`, headers)
      .then(r => setStats(r.data)).catch(console.error);
    axios.get(`${API}/admin/low-stock`, headers)
      .then(r => setLowStock(r.data)).catch(console.error);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentional: fetch once on mount; authHeader() reads localStorage fresh each call

  // Refresh notifications when dashboard mounts
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, []); // intentional: trigger once on mount only

  const skeleton = (cols = 3) => (
    <div className={`grid grid-cols-2 sm:grid-cols-${cols} gap-3`}>
      {Array.from({ length: cols }).map((_, i) => (
        <div key={i} className="h-24 rounded-xl border border-[#273A2F] animate-pulse" style={{ backgroundColor: '#16201A' }} />
      ))}
    </div>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl" data-testid="admin-dashboard">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl font-bold text-[#F2F4F3]">Dashboard</h1>
          <p className="text-sm text-[#A8BCAE] mt-1">PIIKK Agro Farm Fresh Mushrooms · Admin Panel</p>
        </div>
        <Link to="/admin/mushrooms/new" data-testid="dashboard-add-mushroom-btn">
          <button className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white transition-all hover:-translate-y-0.5" style={{ backgroundColor: '#D97A46' }}>
            <Plus className="w-4 h-4" /> Add Mushroom
          </button>
        </Link>
      </div>

      {/* ── LOW STOCK ALERT ── */}
      {lowStock?.length > 0 && (
        <div className="mb-6 rounded-xl border overflow-hidden" style={{ borderColor: 'rgba(245,158,11,0.35)', backgroundColor: 'rgba(245,158,11,0.06)' }}
          data-testid="low-stock-alert">
          <div className="flex items-center gap-2.5 px-4 py-3 border-b" style={{ borderColor: 'rgba(245,158,11,0.25)' }}>
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
            <span className="text-sm font-semibold text-amber-400">Low Stock Alert</span>
            <span className="ml-auto text-xs text-amber-500 bg-amber-500/15 px-2 py-0.5 rounded-full font-medium">
              {lowStock.length} item{lowStock.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="divide-y" style={{ '--tw-divide-color': 'rgba(245,158,11,0.12)' }}>
            {lowStock.map((m, i) => (
              <Link key={m.id || i} to="/admin/mushrooms">
                <div className="flex items-center justify-between px-4 py-2.5 hover:bg-white/5 transition-colors" data-testid={`low-stock-item-${m.id}`}>
                  <span className="text-sm text-[#F2F4F3]">{m.name}</span>
                  <div className="flex items-center gap-2">
                    {!m.isActive && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-700 text-zinc-400">Inactive</span>
                    )}
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${m.qty === 0 ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/15 text-amber-400'}`}>
                      {m.qty === 0 ? 'Out of stock' : `${m.qty} ${m.uom || '250g'} left`}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* ── SECTION 1: INVENTORY ── */}
      <SectionHeader title="Inventory" link="/admin/mushrooms" linkLabel="Manage Mushrooms →" />
      {!stats ? skeleton(3) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <StatTile label="Total Mushrooms" value={stats.mushrooms} icon={Leaf} color="#D97A46" link="/admin/mushrooms" />
          <StatTile label="Active (Visible)" value={stats.mushrooms_active} icon={ToggleRight} color="#10b981" link="/admin/mushrooms" sub="Shown in shop" />
          <StatTile label="Pre-Order Only" value={stats.upcoming} icon={Clock} color="#f59e0b" link="/admin/mushrooms" sub="isUpcoming items" />
        </div>
      )}

      {/* ── SECTION 2: PRE-ORDERS ── */}
      <SectionHeader title="Pre-Orders" link="/admin/preorders" linkLabel="Manage Pre-Orders →" />
      {!stats ? skeleton(4) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatTile label="Total Pre-Orders" value={stats.preorders} icon={ShoppingBag} color="#818cf8" link="/admin/preorders" />
          <StatTile label="Pending" value={pending.preorders} icon={AlertCircle} color="#f59e0b" link="/admin/preorders" sub="Awaiting confirmation" />
          <StatTile label="Confirmed" value={stats.preorders_confirmed} icon={CheckCircle} color="#10b981" link="/admin/preorders" sub="Ready to fulfill" />
          <StatTile label="Cancelled" value={stats.preorders_cancelled} icon={XCircle} color="#ef4444" link="/admin/preorders" sub="Closed" />
        </div>
      )}

      {/* ── SECTION 3: CART ORDERS ── */}
      <SectionHeader title="Cart Orders" link="/admin/orders" linkLabel="Manage Orders →" />
      {!stats ? skeleton(4) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatTile label="Total Cart Orders" value={stats.orders} icon={ShoppingCart} color="#34d399" link="/admin/orders" />
          <StatTile label="Pending" value={pending.orders} icon={AlertCircle} color="#f59e0b" link="/admin/orders" sub="Awaiting confirmation" />
          <StatTile label="Confirmed" value={stats.orders_confirmed} icon={CheckCircle} color="#10b981" link="/admin/orders" sub="Ready to fulfill" />
          <StatTile label="Cancelled" value={stats.orders_cancelled} icon={XCircle} color="#ef4444" link="/admin/orders" sub="Closed" />
        </div>
      )}

      {/* ── QUICK NAV CARDS ── */}
      <SectionHeader title="Quick Access" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { title: "Manage Mushrooms", desc: "Add, edit, update prices, stock and active/upcoming status", link: "/admin/mushrooms", icon: Leaf, color: "#D97A46" },
          { title: "Pre-Orders", desc: "View and update customer pre-orders with date and status", link: "/admin/preorders", icon: ShoppingBag, color: "#818cf8" },
          { title: "Cart Orders", desc: "View and manage WhatsApp-initiated cart orders", link: "/admin/orders", icon: ShoppingCart, color: "#34d399" },
          { title: "Podcast Library", desc: "Add YouTube or Spotify podcast links for customers", link: "/admin/podcasts", icon: Headphones, color: "#f472b6" },
        ].map(({ title, desc, link, icon: Icon, color }) => (
          <Link key={title} to={link}>
            <div className="p-5 rounded-2xl border border-[#273A2F] hover:border-[#D97A46]/30 transition-all group h-full" style={{ backgroundColor: '#16201A' }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${color}18` }}>
                <Icon className="w-5 h-5" style={{ color }} />
              </div>
              <h3 className="font-semibold text-[#F2F4F3] text-sm group-hover:text-[#D97A46] transition-colors">{title}</h3>
              <p className="text-xs text-[#A8BCAE] mt-1.5 leading-relaxed">{desc}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
